import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { JobStatus } from 'features/myJobs';
import { DEPRECATED_OFFER_ERROR_CODES, RejectPayload, rejectShifts, rejectWeeks } from 'pages/candidateOffer';
import { Offer } from 'pages/candidateOffer/types';
import { ApiError, ErrorCode } from 'shared/api';
import { LoadingStatus } from 'shared/types';
import { RootState } from 'store';

import { fetchOffers, FetchOffersResponse } from '../api';
import { compareProcessedOffers, isSameOffer, offerToListOffer } from '../helpers';
import { DeclineOfferState, ListOffer, ProcessedListOffer } from '../types';

interface LoadOffersThunkArgs {
    pageNumber?: number;
    append?: boolean;
}

interface DeclineOfferThunkArgs {
    offer: ListOffer | Offer;
}

interface AddProcessedOfferPayload {
    processedOffer: ProcessedListOffer;
}

interface AddProcessedOfferByTokenPayload {
    offer: ListOffer | Offer;
    offerStatus: JobStatus;
}

interface DeleteOfferPayload {
    orderId: string;
    orderRoleId: string;
}

interface OpenDeclineDialogPayload {
    offer: ListOffer | Offer;
}

interface CandidateOffersState {
    offers: ListOffer[];
    processedOffers: ProcessedListOffer[];
    status: LoadingStatus;
    loadMoreStatus: LoadingStatus;
    hasMorePages: boolean;
    currentPage: number;
    totalOpenJobs: number;
    newThisWeek: number;
    declineOfferState: DeclineOfferState | null;
    acceptOfferState: DeclineOfferState | null;
    error?: ErrorCode;
}

const initialState: CandidateOffersState = {
    offers: [],
    processedOffers: [],
    status: 'idle',
    loadMoreStatus: 'idle',
    hasMorePages: false,
    currentPage: 0,
    totalOpenJobs: 0,
    newThisWeek: 0,
    declineOfferState: null,
    acceptOfferState: null
};

const candidateOfferListSlice = createSlice({
    name: 'candidateOffers',
    initialState,
    reducers: {
        addProcessedOffer: (state, { payload: { processedOffer } }: PayloadAction<AddProcessedOfferPayload>) => {
            state.offers = state.offers.filter((offer) => !isSameOffer(offer, processedOffer));
            state.processedOffers.push(processedOffer);
            state.processedOffers.sort(compareProcessedOffers);
        },
        deleteOffer: (state, { payload }: PayloadAction<DeleteOfferPayload>) => {
            const { orderId, orderRoleId } = payload;

            state.offers = state.offers.filter((offer) => !isSameOffer(offer, { orderId, orderRoleId }));
        },
        openDeclineDialog: (state, { payload: { offer } }: PayloadAction<OpenDeclineDialogPayload>) => {
            state.declineOfferState = {
                status: 'idle',
                offer
            };
        },
        closeDeclineDialog: (state) => {
            state.declineOfferState = null;
        },
        openAcceptDialog: (state, { payload: { offer } }: PayloadAction<OpenDeclineDialogPayload>) => {
            state.acceptOfferState = {
                status: 'idle',
                offer
            };
        },
        closeAcceptDialog: (state) => {
            state.acceptOfferState = null;
        },
        resetState: () => initialState
    },
    extraReducers: (builder) => {
        builder.addCase(loadOffersThunk.rejected, (state, { payload: errorCode }) => {
            state.error = errorCode;
            state.status = 'error';
        });

        builder.addCase(loadOffersThunk.pending, (state) => {
            state.status = 'pending';
        });

        builder.addCase(loadOffersThunk.fulfilled, (state, { payload }) => {
            const { response, pageNumber, append } = payload;
            const { items, hasMorePages, totalOpenJobs, newThisWeek } = response;

            state.status = 'success';
            state.hasMorePages = hasMorePages;
            state.currentPage = pageNumber;
            state.totalOpenJobs = totalOpenJobs;
            state.newThisWeek = newThisWeek;

            if (append) {
                state.offers = [...state.offers, ...items];
            } else {
                state.offers = items;
            }
        });

        builder.addCase(loadMoreOffersThunk.rejected, (state, { payload: errorCode }) => {
            state.error = errorCode;
            state.loadMoreStatus = 'error';
        });

        builder.addCase(loadMoreOffersThunk.pending, (state) => {
            state.loadMoreStatus = 'pending';
        });

        builder.addCase(loadMoreOffersThunk.fulfilled, (state, { payload }) => {
            const { response, pageNumber } = payload;
            const { items, hasMorePages, totalOpenJobs, newThisWeek } = response;

            state.loadMoreStatus = 'success';
            state.hasMorePages = hasMorePages;
            state.currentPage = pageNumber;
            state.totalOpenJobs = totalOpenJobs;
            state.newThisWeek = newThisWeek;
            state.offers = [...state.offers, ...items];
        });

        builder.addCase(declineOfferThunk.pending, (state) => {
            if (!state.declineOfferState) {
                return;
            }

            state.declineOfferState.status = 'pending';
            state.declineOfferState.error = undefined;
        });

        builder.addCase(declineOfferThunk.rejected, (state, { payload: errorCode }) => {
            if (!state.declineOfferState) {
                return;
            }

            state.declineOfferState.error = errorCode;
            state.declineOfferState.status = 'error';
        });

        builder.addCase(declineOfferThunk.fulfilled, (state) => {
            state.declineOfferState = null;
        });
    }
});

export const {
    addProcessedOffer,
    closeDeclineDialog,
    closeAcceptDialog,
    openAcceptDialog,
    deleteOffer,
    openDeclineDialog,
    resetState
} = candidateOfferListSlice.actions;

export const addProcessedOfferByToken = createAsyncThunk<
void,
AddProcessedOfferByTokenPayload,
{ state: RootState }
>(
    'candidateOffers/addProcessedOfferByToken',
    ({ offer, offerStatus }, thunkAPI) => {
        const orderId = 'orderId' in offer ? offer.orderId : offer.id;
        const orderRoleId = offer.orderRoleId;

        const { offers } = thunkAPI.getState().candidateOfferList;
        const listOffer = offers.find((o) => isSameOffer(o, { orderId, orderRoleId }))
            ?? offerToListOffer(offer);

        thunkAPI.dispatch(addProcessedOffer({
            processedOffer: {
                ...listOffer,
                status: offerStatus
            }
        }));
    }
);

interface LoadOffersThunkResult {
    response: FetchOffersResponse;
    pageNumber: number;
    append: boolean;
}

export const loadOffersThunk = createAsyncThunk<
LoadOffersThunkResult,
LoadOffersThunkArgs,
{ state: RootState; rejectValue: ErrorCode }
>(
    'candidateOffers/loadOffers',
    async ({ pageNumber = 1, append = false }, thunkAPI) => {
        const response = await fetchOffers(pageNumber);

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return { response, pageNumber, append };
    }
);

export const loadMoreOffersThunk = createAsyncThunk<
LoadOffersThunkResult,
void,
{ state: RootState; rejectValue: ErrorCode }
>(
    'candidateOffers/loadMoreOffers',
    async (_, thunkAPI) => {
        const state = thunkAPI.getState();
        const { currentPage, hasMorePages } = state.candidateOfferList;

        if (!hasMorePages) {
            return thunkAPI.rejectWithValue('No more pages');
        }

        const nextPage = currentPage + 1;
        const response = await fetchOffers(nextPage);

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return { response, pageNumber: nextPage, append: true };
    }
);

export const declineOfferThunk = createAsyncThunk<
void,
DeclineOfferThunkArgs,
{ state: RootState; rejectValue: ErrorCode }
>(
    'candidateOffers/declineOffer',
    async ({ offer }, thunkAPI) => {
        let promise: Promise<void | ApiError>;

        const { orderId, offerType } = 'offerType' in offer
            ? { orderId: offer.orderId, offerType: offer.offerType }
            : {
                orderId: offer.id,
                offerType: offer.shifts?.length ? 'Shifts' : 'Weeks'
            };

        const payload: RejectPayload = {
            orderId: orderId,
            orderRoleId: offer.orderRoleId
        };

        switch (offerType) {
            case 'Shifts':
                promise = rejectShifts(payload);
                break;
            case 'Weeks':
                promise = rejectWeeks(payload);
                break;
            default:
                return thunkAPI.rejectWithValue('Unknown offer type');
        }

        const state = thunkAPI.getState().candidateOfferList;
        const listOffer = state.offers.find((listOffer) => isSameOffer(listOffer, { orderId, orderRoleId: offer.orderRoleId }))
            ?? offerToListOffer(offer);

        const response = await promise;

        if (response instanceof ApiError) {
            const { errorCode } = response;
            const skipDeprecatedError = DEPRECATED_OFFER_ERROR_CODES.some((deprecatedOfferCode) => deprecatedOfferCode === errorCode);

            if (!skipDeprecatedError) {
                return thunkAPI.rejectWithValue(errorCode);
            }
        }

        thunkAPI.dispatch(addProcessedOffer({
            processedOffer: {
                ...listOffer,
                status: 'declinedByCandidate'
            }
        }));
    }
);

export default candidateOfferListSlice.reducer;
